import axios, { AxiosResponse, AxiosError } from 'axios';
import { 
  PDFFormData, 
  ApiResponse, 
  UploadResponse, 
  PDFFilesResponse,
  TemplateResponse,
  TemplatesResponse,
  Template,
  TemplatePreviewResponse
} from '../types';
import { API_ENDPOINTS, ERROR_MESSAGES } from '../constants';

// 创建axios实例
const api = axios.create({
  timeout: 30000, // 30秒超时
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    console.log(`API请求: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`API响应: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    console.error('API响应错误:', error);
    
    // 处理网络错误
    if (!error.response) {
      throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
    }
    
    // 处理服务器错误
    const errorData = error.response.data as { error?: string };
    const errorMessage = errorData?.error || error.message;
    throw new Error(errorMessage);
  }
);

/**
 * 上传文件
 */
export const uploadFiles = async (files: File[]): Promise<UploadResponse> => {
  const formData = new FormData();
  
  files.forEach(file => {
    formData.append('images', file);
  });
  
  try {
    const response = await api.post<UploadResponse>(
      API_ENDPOINTS.UPLOAD, 
      formData, 
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('文件上传失败:', error);
    throw new Error(ERROR_MESSAGES.UPLOAD_FAILED);
  }
};

/**
 * 生成PDF
 */
export const generatePDF = async (
  formData: PDFFormData, 
  imageUrls: string[]
): Promise<Blob> => {
  try {
    const response = await api.post(
      API_ENDPOINTS.GENERATE_PDF,
      { formData, imageUrls },
      { responseType: 'blob' }
    );
    
    return response.data;
  } catch (error) {
    console.error('PDF生成失败:', error);
    throw new Error(ERROR_MESSAGES.PDF_GENERATION_FAILED);
  }
};

/**
 * 获取PDF文件列表
 */
export const getPDFFiles = async (): Promise<PDFFilesResponse> => {
  try {
    const response = await api.get<PDFFilesResponse>(API_ENDPOINTS.PDF_FILES);
    return response.data;
  } catch (error) {
    console.error('获取文件列表失败:', error);
    throw new Error(ERROR_MESSAGES.FETCH_FILES_FAILED);
  }
};

/**
 * 下载PDF文件
 */
export const downloadPDF = async (filename: string): Promise<Blob> => {
  try {
    const response = await api.get(
      `${API_ENDPOINTS.DOWNLOAD_PDF}/${encodeURIComponent(filename)}`,
      { responseType: 'blob' }
    );
    
    return response.data;
  } catch (error) {
    console.error('下载文件失败:', error);
    throw new Error(`下载文件失败: ${error}`);
  }
};

/**
 * 删除PDF文件
 */
export const deletePDF = async (filename: string): Promise<ApiResponse> => {
  try {
    const response = await api.delete<ApiResponse>(
      `${API_ENDPOINTS.PDF_FILES}/${encodeURIComponent(filename)}`
    );
    
    return response.data;
  } catch (error) {
    console.error('删除文件失败:', error);
    throw new Error(`删除文件失败: ${error}`);
  }
};

/**
 * 健康检查
 */
export const healthCheck = async (): Promise<ApiResponse> => {
  try {
    const response = await api.get<ApiResponse>(API_ENDPOINTS.HEALTH);
    return response.data;
  } catch (error) {
    console.error('健康检查失败:', error);
    throw new Error('服务器连接失败');
  }
};

/**
 * 创建新模板
 */
export const createTemplate = async (templateData: {
  name?: string;
  content: string;
  type?: 'html' | 'json';
}): Promise<TemplateResponse> => {
  try {
    const response = await api.post<TemplateResponse>(
      API_ENDPOINTS.TEMPLATES, 
      templateData
    );
    
    return response.data;
  } catch (error) {
    console.error('创建模板失败:', error);
    throw new Error(ERROR_MESSAGES.TEMPLATE_CREATE_FAILED);
  }
};

/**
 * 获取模板列表
 */
export const getTemplates = async (): Promise<TemplatesResponse> => {
  try {
    const response = await api.get<TemplatesResponse>(
      API_ENDPOINTS.TEMPLATES
    );
    
    return response.data;
  } catch (error) {
    console.error('获取模板列表失败:', error);
    throw new Error(ERROR_MESSAGES.FETCH_TEMPLATES_FAILED);
  }
};

/**
 * 获取单个模板详情
 */
export const getTemplateById = async (templateId: string): Promise<TemplateResponse> => {
  try {
    const response = await api.get<TemplateResponse>(
      `${API_ENDPOINTS.TEMPLATES}/${templateId}`
    );
    
    return response.data;
  } catch (error) {
    console.error('获取模板详情失败:', error);
    throw new Error(ERROR_MESSAGES.FETCH_TEMPLATE_FAILED);
  }
};

/**
 * 更新模板
 */
export const updateTemplate = async (
  templateId: string, 
  updateData: Partial<Template>
): Promise<TemplateResponse> => {
  try {
    const response = await api.put<TemplateResponse>(
      `${API_ENDPOINTS.TEMPLATES}/${templateId}`,
      updateData
    );
    
    return response.data;
  } catch (error) {
    console.error('更新模板失败:', error);
    throw new Error(ERROR_MESSAGES.UPDATE_TEMPLATE_FAILED);
  }
};

/**
 * 删除模板
 */
export const deleteTemplate = async (templateId: string): Promise<ApiResponse> => {
  try {
    const response = await api.delete<ApiResponse>(
      `${API_ENDPOINTS.TEMPLATES}/${templateId}`
    );
    
    return response.data;
  } catch (error) {
    console.error('删除模板失败:', error);
    throw new Error(ERROR_MESSAGES.DELETE_TEMPLATE_FAILED);
  }
};

/**
 * 生成模板预览
 */
export const generateTemplatePreview = async (
  templateId: string
): Promise<TemplatePreviewResponse> => {
  try {
    const response = await api.get<TemplatePreviewResponse>(
      `${API_ENDPOINTS.TEMPLATES}/${templateId}/preview`
    );
    
    return response.data;
  } catch (error) {
    console.error('生成模板预览失败:', error);
    throw new Error(ERROR_MESSAGES.GENERATE_PREVIEW_FAILED);
  }
};

export default api;
